import { FocusError } from "focus-formik-error";
import { useFormik } from "formik";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useMailChimp } from "react-use-mailchimp-signup";
import { actions as flashesActions } from "store/flashes";
import { englishOnlyEmail } from "utils/helpers";
import vars from "utils/vars";
import { object, string } from "yup";

const KeepInTouch = () => {
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const {
		error: mailchimpError,
		status: mailchimpStatus,
		subscribe: mailchimpSubscribe,
		message: mailchimpMessage,
	} = useMailChimp({ action: vars.secrets.mailchimp.url });

	// event handlers
	const onKeepInTouchFormikSubmitHandler = useCallback(
		async ({ EMAIL, ...values }, { setSubmitting, resetForm }) => {
			const lowerCaseEmail = EMAIL.toLowerCase();
			await mailchimpSubscribe({ ...values, EMAIL: lowerCaseEmail });
			setSubmitting(false);
			resetForm();
		},
		[mailchimpSubscribe]
	);

	// formik
	const keepInTouchFormik = useFormik({
		validateOnBlur: false,
		initialValues: { EMAIL: "" },
		validationSchema: object().shape({
			EMAIL: string()
				.required(t("system_messages.required_field"))
				.email(t("system_messages.valid_email"))
				.matches(englishOnlyEmail, t("system_messages.english_only_email")),
		}),
		onSubmit: onKeepInTouchFormikSubmitHandler,
	});

	// effect hooks
	useEffect(() => {
		if (mailchimpMessage)
			dispatch(
				flashesActions.add({
					type: mailchimpError ? "danger" : "success",
					message: mailchimpMessage,
					id: mailchimpMessage,
				})
			);
	}, [dispatch, mailchimpError, mailchimpMessage]);

	return (
		<form onSubmit={keepInTouchFormik.handleSubmit} noValidate>
			<FocusError formik={keepInTouchFormik} />
			<fieldset disabled={keepInTouchFormik.isSubmitting || mailchimpStatus === "sending"}>
				<legend className="visually-hidden">Keep In Touch Form:</legend>
				<div
					className={`input-group position-relative align-items-stretch flex-nowrap p-1 rounded-pill bg-body border border-2 ${
						!!keepInTouchFormik?.touched?.EMAIL && !!keepInTouchFormik?.errors?.EMAIL
							? "border-danger"
							: keepInTouchFormik?.values?.EMAIL &&
							  !!keepInTouchFormik?.touched?.EMAIL &&
							  !!!keepInTouchFormik?.errors?.EMAIL
							? "border-success"
							: "border-primary"
					}`}>
					<div className="flex-grow-1">
						<label
							htmlFor="keepInTouchEmailField"
							className="form-label text-capitalize border-0 mb-0 text-secondary visually-hidden">
							<small>
								{t("fields.email")} <span className="ms-1">*</span>
							</small>
						</label>
						<input
							onChange={keepInTouchFormik.handleChange}
							onBlur={keepInTouchFormik.handleBlur}
							value={keepInTouchFormik?.values?.EMAIL || ""}
							type="email"
							name="EMAIL"
							id="keepInTouchEmailField"
							className={`form-control rounded-start-pill px-3 text-truncate bg-transparent border-0 ${
								!!keepInTouchFormik?.touched?.EMAIL && !!keepInTouchFormik?.errors?.EMAIL
									? "is-invalid"
									: keepInTouchFormik?.values?.EMAIL &&
									  !!keepInTouchFormik?.touched?.EMAIL &&
									  !!!keepInTouchFormik?.errors?.EMAIL
									? "is-valid"
									: ""
							}`}
							placeholder="Email"
							autoComplete="email"
						/>
						{!!keepInTouchFormik?.touched?.EMAIL && !!keepInTouchFormik?.errors?.EMAIL && (
							<div className="invalid-tooltip text-uppercase mx-3 mt-1">
								<strong
									dangerouslySetInnerHTML={{
										__html: mailchimpMessage || keepInTouchFormik.errors.EMAIL,
									}}></strong>
							</div>
						)}
					</div>
					<div
						className={`vr border border-start-0 bg-transparent op-100 mx-1 ${
							!!keepInTouchFormik?.touched?.EMAIL && !!keepInTouchFormik?.errors?.EMAIL
								? "border-danger"
								: keepInTouchFormik?.values?.EMAIL &&
								  !!keepInTouchFormik?.touched?.EMAIL &&
								  !!!keepInTouchFormik?.errors?.EMAIL
								? "border-success"
								: "border-primary"
						}`}></div>
					<div className="input-group-prepend">
						<button
							className="btn btn-link rounded-end-pill px-4 h-100 border-0 text-uppercase text-decoration-none"
							type="submit"
							disabled={!keepInTouchFormik.isValid}>
							{mailchimpStatus === "sending" ? (
								<span className="spinner-border spinner-border-sm" role="status">
									<span className="visually-hidden">{`${t("content.loading")}...`}</span>
								</span>
							) : (
								<strong>
									<small>{t("actions.subscribe")}</small>
								</strong>
							)}
						</button>
					</div>
				</div>
			</fieldset>
		</form>
	);
};

export default KeepInTouch;
